import React from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";

import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { useGroupEmitter } from "~/lib/websocket";
import DislikeModal from "~/meetingRecorder/DislikeModal";
import { clearLikedSectionCacheForRecording } from "~/meetingRecorder/HighlightsPanel";
import { TranscriptModalContext } from "~/meetingRecorder/TranscriptModalContext";
import { type HighlightFeedbackSource, processRecordingCommentUpdates } from "~/meetingRecorder/utils";
import { useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import { useRefetchMeetingRecording } from "./useRefetchMeetingRecording";

import type { useMeetingRecordingWebsocket_meetingRecording$key } from "~/__generated__/useMeetingRecordingWebsocket_meetingRecording.graphql";
import type { useMeetingRecordingWebsocketRegenerateFeedbackMutation } from "~/__generated__/useMeetingRecordingWebsocketRegenerateFeedbackMutation.graphql";

// GraphQL queries for websocket refetching
const highlightsRefetchQuery = graphql`
	query useMeetingRecordingWebsocketHighlightsRefetchQuery($recordingId: ID!) {
		meetingRecording: node(id: $recordingId) {
			...HighlightsPanel_meetingRecording @arguments(public: false)
			...ChaptersPanel_meetingRecording
		}
	}
`;

const clipsRefetchQuery = graphql`
	query useMeetingRecordingWebsocketClipsRefetchQuery($recordingId: ID!) {
		meetingRecording: node(id: $recordingId) {
			__typename
			... on MeetingRecording {
				clips {
					id
					clipStart
					clipEnd
					...ClipsPanel_ClipSummary_clip
				}
				...ClipsPanel_meetingRecording
			}
		}
	}
`;

// Socket payload types
type SocketPayload =
	| { type: "SUMMARY_SECTION_EDIT_RECEIVED"; data: { updated_section: string } }
	| {
			type: "COMMENTS_CHANGED";
			data: {
				threadId: string;
				startTimestamp: string;
				endTimestamp: string;
				count: number;
				users: Array<{ id: string; email: string; fullName: string; image: string }>;
				targetUsers: Array<{ id: string; fullName: string }>;
				commentOperation: string;
			};
	  }
	| { type: "MEETING_RECORDING_REGENERATION_FAILED"; data: { user_id_who_regenerated: string } }
	| { type: "MEETING_RECORDING_CLIP_GENERATED"; data: Record<string, unknown> }
	| { type: "MEETING_RECORDING_REGENERATION_COMPLETE"; data: Record<string, unknown> }
	| { type: "MEETING_RECORDING_TEMPLATE_GENERATION_COMPLETE"; data: Record<string, unknown> }
	| { type: "MEETING_RECORDING_TEMPLATE_GENERATION_FAILED"; data: { user_id_who_regenerated: string } }
	| { type: "MEETING_RECORDING_STATS_GENERATION_COMPLETE"; data: Record<string, unknown> };

interface UseMeetingRecordingWebsocketArguments {
	meetingRecordingKey: useMeetingRecordingWebsocket_meetingRecording$key | null;
	userId?: string;
}

interface UseMeetingRecordingWebsocketReturn {
	regenerating: boolean;
	onRegenerateClicked: (source: HighlightFeedbackSource) => Promise<void>;
	generatingRecapTemplate: boolean;
	recapTemplateType: string | undefined;
	aiDetectedActionItemsListRefetchKey: number;
	setAiDetectedActionItemsListRefetchKey: React.Dispatch<React.SetStateAction<number>>;
}

const regenerateSummaryMutation = graphql`
	mutation useMeetingRecordingWebsocketRegenerateFeedbackMutation($meetingRecordingId: ID!) {
		regenerateSummary(meetingRecordingId: $meetingRecordingId) {
			ok
		}
	}
`;

/**
 * Hook for managing real-time websocket updates for meeting recordings.
 *
 * Establishes a persistent websocket connection to receive live updates about meeting recording
 * changes. Handles summary edits, comment changes, clip generation, and regeneration events.
 * Makes GraphQL refetch queries and updates the Relay store directly for certain events.
 *
 * **Side Effects:** Sets up websocket connection, makes external GraphQL requests,
 * updates Relay store, shows toast notifications.
 *
 * **Failure Modes:** Silent failures for GraphQL refetches, no connection without recordingId,
 * websocket connection issues prevent real-time updates.
 *
 * @param meetingRecordingKey - Key of the meeting recording to monitor for updates
 * @param userId - Current user's ID, used to filter certain notifications
 * @returns Object containing:
 *   - `regenerating`: boolean - Whether meeting recording regeneration is in progress
 *   - `onRegenerateClicked`: () => void - Callback to confirm and regenerate the meeting recording
 *   - `generatingRecapTemplate`: boolean - Whether recap template generation is in progress
 *   - `recapTemplateType`: string | undefined - Type of recap template being generated
 *   - `aiDetectedActionItemsListRefetchKey`: number - Key that increments to trigger action items refetch
 */
export function useMeetingRecordingWebsocket({
	meetingRecordingKey,
	userId,
}: UseMeetingRecordingWebsocketArguments): UseMeetingRecordingWebsocketReturn {
	// State for websocket-related functionality
	const meetingRecording = useFragment(
		graphql`
			fragment useMeetingRecordingWebsocket_meetingRecording on MeetingRecording {
				id
				title
				redactionIsInProgress
				isGeneratingRecapTemplate
				regenerationForLanguageIsInProgress
				...DislikeModal_meetingRecording
			}
		`,
		meetingRecordingKey,
	);
	const selectedRecordingId = meetingRecording?.id;

	const modals = useModals();
	const { analyticsFingerprint } = React.useContext(TranscriptModalContext);
	const [aiDetectedActionItemsListRefetchKey, setAiDetectedActionItemsListRefetchKey] = React.useState(0);
	const [regenerating, setRegenerating] = React.useState(
		(meetingRecording?.regenerationForLanguageIsInProgress || meetingRecording?.redactionIsInProgress) ?? false,
	);
	const [generatingRecapTemplate, setGeneratingRecapTemplate] = React.useState(
		meetingRecording?.isGeneratingRecapTemplate ?? false,
	);
	const [recapTemplateType, setRecapTemplateType] = React.useState<string | undefined>(undefined);
	const environment = useRelayEnvironment();
	const refetchMeetingRecording = useRefetchMeetingRecording();

	// Sync local state with fragment data changes
	React.useEffect(() => {
		const isRegenerating = Boolean(
			meetingRecording?.regenerationForLanguageIsInProgress || meetingRecording?.redactionIsInProgress,
		);
		setRegenerating(isRegenerating);
	}, [meetingRecording?.regenerationForLanguageIsInProgress, meetingRecording?.redactionIsInProgress]);

	React.useEffect(() => {
		setGeneratingRecapTemplate(meetingRecording?.isGeneratingRecapTemplate ?? false);
	}, [meetingRecording?.isGeneratingRecapTemplate]);

	// Regeneration callbacks
	const successRegeneration = React.useCallback(() => {
		if (meetingRecording?.id) {
			refetchMeetingRecording(meetingRecording.id);
		}

		setAiDetectedActionItemsListRefetchKey(c => c + 1);
		setRegenerating(false);
		toast.success("Recap successfully regenerated");
	}, [meetingRecording?.id, refetchMeetingRecording]);

	const errorRegeneration = React.useCallback(() => {
		setRegenerating(false);
		toast.error("Failed to regenerate recap");
	}, []);

	const onRegenerate = React.useCallback(() => {
		if (!meetingRecording) return;

		setRegenerating(true);
		commitMutationPromise<useMeetingRecordingWebsocketRegenerateFeedbackMutation>(environment, {
			mutation: regenerateSummaryMutation,
			variables: {
				meetingRecordingId: meetingRecording.id,
			},
			onError: () => {
				errorRegeneration();
			},
		});
		clearLikedSectionCacheForRecording(meetingRecording.id);
	}, [meetingRecording, environment, errorRegeneration]);

	const onDislike = React.useCallback(
		async (source: HighlightFeedbackSource) => {
			if (!meetingRecording) return;

			const feedbackResult: { feedback: string; otherFeedback: string } | null = await modals.open(({ close }) => (
				<DislikeModal meetingRecording={meetingRecording} source={source} onRegenerate={onRegenerate} close={close} />
			));
			dispatchAnalyticsTrack("[AI Copilot] Dislike Clicked", {
				fingerprint: analyticsFingerprint,
				section: source,
				recordingId: meetingRecording.id,
				recordingTitle: meetingRecording.title,
				feedback: feedbackResult,
			});
		},
		[modals, analyticsFingerprint, meetingRecording, onRegenerate],
	);

	// Websocket message handler
	const onSocketMessage = React.useCallback(
		async (payload: SocketPayload) => {
			if (payload.type === "SUMMARY_SECTION_EDIT_RECEIVED") {
				if (selectedRecordingId) {
					fetchQuery(environment, highlightsRefetchQuery, {
						recordingId: selectedRecordingId,
					}).toPromise();
				}
				setAiDetectedActionItemsListRefetchKey(c => c + 1);
			} else if (payload.type === "COMMENTS_CHANGED") {
				if (selectedRecordingId) {
					processRecordingCommentUpdates(environment, payload, selectedRecordingId, userId);
				}
			} else if (payload.type === "MEETING_RECORDING_CLIP_GENERATED") {
				if (selectedRecordingId) {
					fetchQuery(environment, clipsRefetchQuery, {
						recordingId: selectedRecordingId,
					}).toPromise();
				}
			} else if (payload.type === "MEETING_RECORDING_REGENERATION_COMPLETE") {
				successRegeneration();
			} else if (
				payload.type === "MEETING_RECORDING_REGENERATION_FAILED" &&
				// We only want to show the error message for the user that started the regeneration
				payload.data.user_id_who_regenerated === userId
			) {
				errorRegeneration();
			} else if (payload.type === "MEETING_RECORDING_TEMPLATE_GENERATION_COMPLETE") {
				if (selectedRecordingId) {
					fetchQuery(environment, highlightsRefetchQuery, {
						recordingId: selectedRecordingId,
					})
						.toPromise()
						.then(() => {
							setGeneratingRecapTemplate(false);
							setRecapTemplateType(undefined);
							setAiDetectedActionItemsListRefetchKey(c => c + 1);
							toast.success("Recap template successfully generated");
						});
				}
			} else if (payload.type === "MEETING_RECORDING_TEMPLATE_GENERATION_FAILED") {
				setGeneratingRecapTemplate(false);
				setRecapTemplateType(undefined);
				// We only want to show the error message for the user that tried to generate the template
				if (payload.data.user_id_who_regenerated === userId) {
					toast.error("Failed to generate recap template");
				}
			}
		},
		[userId, selectedRecordingId, successRegeneration, errorRegeneration, environment],
	);

	// Set up websocket connection
	const socket = useGroupEmitter(
		selectedRecordingId ? `meeting_recordings.${selectedRecordingId.split("=").join("")}` : undefined,
	);

	React.useEffect(() => {
		if (!socket) return;

		socket.on("message", onSocketMessage);
		return () => {
			socket.off("message", onSocketMessage);
		};
	}, [onSocketMessage, socket]);

	return {
		regenerating,
		generatingRecapTemplate,
		onRegenerateClicked: onDislike,
		recapTemplateType,
		aiDetectedActionItemsListRefetchKey,
		setAiDetectedActionItemsListRefetchKey,
	};
}
